// Pure value logic for the body-drawing editor. The answer is a `string[4]` of data URLs, "" for
// an untouched view. The array is stored [front, right, left, back] (server mfDocumentAdapter /
// patient-document-html.utils, mobile BodyDrawing's canvas order) while the PDF prints FRONT,
// RIGHT, BACK, LEFT, so always go through BODY_DRAWING_STORAGE_INDEX — never the view's position.

/** Printed (and displayed) order. */
export const BODY_DRAWING_VIEWS = ["FRONT", "RIGHT", "BACK", "LEFT"] as const;

export type BodyDrawingView = typeof BODY_DRAWING_VIEWS[number];

export type BodyDrawingAnswer = [string, string, string, string];

/** Index of each view in the stored answer array. */
export const BODY_DRAWING_STORAGE_INDEX: Record<BodyDrawingView, number> = {
  FRONT: 0,
  RIGHT: 1,
  LEFT: 2,
  BACK: 3,
};

function viewValue(item: unknown): string {
  return typeof item === "string" && item.trim() !== "" ? item : "";
}

/**
 * Always four strings in stored order. Mobile writes short arrays (only up to the last drawn view)
 * and may leave holes, so missing / non-string / blank items become "".
 */
export function normalizeBodyDrawingAnswer(answer: unknown): BodyDrawingAnswer {
  const items = Array.isArray(answer) ? (answer as unknown[]) : [];
  return [viewValue(items[0]), viewValue(items[1]), viewValue(items[2]), viewValue(items[3])];
}

/** The stored drawing of `view`, "" when untouched. */
export function bodyDrawingViewValue(answer: unknown, view: BodyDrawingView): string {
  return normalizeBodyDrawingAnswer(answer)[BODY_DRAWING_STORAGE_INDEX[view]];
}

/** The answer to save after drawing `view` (the other views are kept). */
export function setBodyDrawingView(
  answer: unknown,
  view: BodyDrawingView,
  dataUrl: string
): BodyDrawingAnswer {
  const next = normalizeBodyDrawingAnswer(answer);
  next[BODY_DRAWING_STORAGE_INDEX[view]] = dataUrl;
  return next;
}
